/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

const CALLBACK_LOOP_KEY = "auth_callback_attempt";
export const MAX_CALLBACK_ATTEMPTS = 2;
const CALLBACK_ATTEMPT_WINDOW_MS = 30_000;

/**
 * Detects whether the current URL carries an OAuth `code` query parameter,
 * indicating an in-flight authorization-code callback.
 *
 * @returns True when a `code` parameter is present.
 */
export function hasOAuthCodeInUrl(): boolean {
  const params = new URLSearchParams(window.location.search);
  return !!params.get("code");
}

/**
 * Reads the current callback-attempt count from session storage, resetting to
 * zero when the tracking window has elapsed or the value is unreadable. Used to
 * break OAuth redirect loops.
 *
 * @returns The attempts recorded within the active window.
 */
/**
 * Reads the number of recent OAuth callback attempts from session storage, resetting
 * to zero when the tracking window has elapsed or the record is unreadable. Used to
 * detect and break callback redirect loops.
 *
 * @returns The current attempt count within the active window.
 */
export function getCallbackAttemptCount(): number {
  try {
    const raw = sessionStorage.getItem(CALLBACK_LOOP_KEY);
    if (!raw) return 0;
    const { count, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > CALLBACK_ATTEMPT_WINDOW_MS) return 0;
    return count;
  } catch {
    return 0;
  }
}

/**
 * Increments and persists the callback-attempt counter (with a fresh
 * timestamp), tolerating unavailable storage.
 *
 * @returns The new attempt count.
 */
/**
 * Records another OAuth callback attempt by incrementing the stored counter with a
 * fresh timestamp, tolerating unavailable session storage.
 *
 * @returns The updated attempt count.
 */
export function incrementCallbackAttempt(): number {
  const current = getCallbackAttemptCount();
  const next = current + 1;
  try {
    sessionStorage.setItem(
      CALLBACK_LOOP_KEY,
      JSON.stringify({ count: next, timestamp: Date.now() })
    );
  } catch { /* storage unavailable */ }
  return next;
}

/**
 * Clears the persisted callback-attempt tracking, called once a callback
 * resolves successfully so future logins start fresh.
 */
/**
 * Clears the OAuth callback attempt counter, called after a successful authentication
 * so future callbacks start from a clean slate. Silently ignores storage errors.
 */
export function clearCallbackAttempts(): void {
  try {
    sessionStorage.removeItem(CALLBACK_LOOP_KEY);
  } catch { /* storage unavailable */ }
}
